/**
 * FontesCard — Card de fonte de dados conforme protótipo fontes.html.
 * Prefixo "Fontes" obrigatório (isolamento de tela /sources).
 *
 * Estados suportados:
 *   - connected    : fonte OK, coleta ativa
 *   - warn         : conectado mas sem dados novos (data-gap interno)
 *   - disconnected : token expirado / OAuth revogado
 *   - data-gap     : coleta parcial com dias sem dados
 *
 * A11y: role=article, aria-label por estado, banners com role=alert,
 * status chip com role=status, todos os botões com aria-label descritivo.
 */

import { t } from '../lib/i18n';

/** Tipo de fonte suportado */
export type FontesSourceType = 'instagram' | 'meta_ads' | 'google_drive';

/** Estado visual do card (mapeado de ApiSourceRecord.status) */
export type FontesCardVariant = 'connected' | 'warn' | 'disconnected' | 'data-gap';

/** O que a CROSS coleta por fonte — copy fiel ao protótipo */
const FEEDS_BY_TYPE: Record<FontesSourceType, readonly string[]> = {
  instagram: [
    t('fontes.feeds.ig.reach'),
    t('fontes.feeds.ig.engagement'),
    t('fontes.feeds.ig.followers'),
    t('fontes.feeds.ig.profileVisits'),
  ],
  meta_ads: [
    t('fontes.feeds.ads.reach'),
    t('fontes.feeds.ads.clicks'),
    t('fontes.feeds.ads.spend'),
    t('fontes.feeds.ads.frequency'),
  ],
  google_drive: [
    t('fontes.feeds.drive.sales'),
    t('fontes.feeds.drive.items'),
    t('fontes.feeds.drive.revenue'),
    t('fontes.feeds.drive.avgTicket'),
  ],
};

/** Ícone Material Symbol por tipo */
const ICON_BY_TYPE: Record<FontesSourceType, string> = {
  instagram: 'photo_camera',
  meta_ads: 'campaign',
  google_drive: 'folder_open',
};

/** Classe de wrapper de ícone por tipo */
function iconWrapClass(type: FontesSourceType, variant: FontesCardVariant): string {
  if (variant === 'disconnected') return 'fontes-icon-wrap fontes-icon-wrap--warn';
  if (type === 'instagram') return 'fontes-icon-wrap fontes-icon-wrap--ig';
  if (type === 'meta_ads') return 'fontes-icon-wrap fontes-icon-wrap--ads';
  return 'fontes-icon-wrap fontes-icon-wrap--drive';
}

/** Props do FontesCard */
export interface FontesCardProps {
  /** source_id da API */
  readonly sourceId: string;
  readonly type: FontesSourceType;
  readonly variant: FontesCardVariant;
  /** Nome da conta / pasta exibido abaixo do nome da fonte */
  readonly accountLabel: string | null;
  /** Texto de "última coleta" já formatado */
  readonly lastSyncLabel: string;
  /** onConnect — inicia OAuth (usado para conectar ou reconectar) */
  readonly onConnect: () => void;
  /** onDisconnect — abre diálogo de confirmação */
  readonly onDisconnect: () => void;
  /** onPickFolder — abre DriveFolderPicker (apenas google_drive conectado sem pasta) */
  readonly onPickFolder?: (() => void) | undefined;
  /** folderId presente quando google_drive já tem pasta selecionada */
  readonly folderId?: string | null | undefined;
  readonly folderName?: string | null;
  /** Índice para delay de stagger (0-based) — usado pela grade pai */
  readonly staggerIndex?: number;
}

/** Nome legível da fonte */
function sourceDisplayName(type: FontesSourceType): string {
  switch (type) {
    case 'instagram':
      return t('onboarding.sources.instagram');
    case 'meta_ads':
      return t('onboarding.sources.metaAds');
    case 'google_drive':
      return t('onboarding.sources.googleDrive');
  }
}

/** Classe de variante para o card raiz */
function cardClass(variant: FontesCardVariant): string {
  const base = 'fontes-card';
  if (variant === 'disconnected') return `${base} ${base}--disconnected`;
  if (variant === 'data-gap') return `${base} ${base}--data-gap`;
  return base;
}

/**
 * Ícone Material Symbols Rounded renderizado como ligatura de fonte.
 * Exige que a classe CSS (fontes-chip-icon / fontes-btn-icon etc.) aplique
 * font-family: 'Material Symbols Rounded' + font-variation-settings.
 * O texto inner é o nome do glifo — a fonte converte para o símbolo.
 */
function MsIcon({
  name,
  className,
}: {
  readonly name: string;
  readonly className: string;
}): JSX.Element {
  return (
    <span
      className={className}
      aria-hidden="true"
      /* data-icon garante selecionabilidade de testes sem depender do texto */
      data-icon={name}
    >
      {name}
    </span>
  );
}

/** Chip de status */
function FontesStatusChip({ variant }: { readonly variant: FontesCardVariant }): JSX.Element {
  if (variant === 'connected') {
    return (
      <div
        className="fontes-status-chip fontes-status-chip--ok"
        role="status"
        aria-label={t('fontes.status.connected')}
      >
        <MsIcon name="check_circle" className="fontes-chip-icon" />
        {t('fontes.status.connected')}
      </div>
    );
  }
  if (variant === 'warn') {
    return (
      <div
        className="fontes-status-chip fontes-status-chip--warn"
        role="status"
        aria-label={t('fontes.status.warn')}
      >
        <MsIcon name="warning" className="fontes-chip-icon" />
        {t('fontes.status.warn')}
      </div>
    );
  }
  if (variant === 'disconnected') {
    return (
      <div
        className="fontes-status-chip fontes-status-chip--error"
        role="status"
        aria-label={t('fontes.status.disconnected')}
      >
        <MsIcon name="warning" className="fontes-chip-icon" />
        {t('fontes.status.disconnected')}
      </div>
    );
  }
  // data-gap
  return (
    <div
      className="fontes-status-chip fontes-status-chip--gap"
      role="status"
      aria-label={t('fontes.status.dataGap')}
    >
      <MsIcon name="warning_amber" className="fontes-chip-icon" />
      {t('fontes.status.dataGap')}
    </div>
  );
}

/** Banner de estado (warn / disconnected / data-gap) */
function FontesStateBanner({
  variant,
  lastSyncLabel,
  type,
  onConnect,
}: {
  readonly variant: FontesCardVariant;
  readonly lastSyncLabel: string;
  readonly type: FontesSourceType;
  readonly onConnect: () => void;
}): JSX.Element | null {
  const name = sourceDisplayName(type);

  if (variant === 'warn') {
    return (
      <div className="fontes-warn-banner" role="alert">
        <MsIcon name="info" className="fontes-banner-icon" />
        <span>{t('fontes.banner.noNewData', { lastSync: lastSyncLabel })}</span>
      </div>
    );
  }
  if (variant === 'disconnected') {
    return (
      <div className="fontes-error-banner" role="alert">
        <MsIcon name="link_off" className="fontes-banner-icon" />
        <span>{t('fontes.banner.expired', { source: name })}</span>
      </div>
    );
  }
  if (variant === 'data-gap') {
    return (
      <div className="fontes-gap-banner" role="alert">
        <MsIcon name="warning_amber" className="fontes-banner-icon" />
        <span>
          {t('fontes.banner.dataGap')}{' '}
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              font: 'inherit',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0,
              marginTop: 4,
              display: 'block',
            }}
            onClick={onConnect}
            aria-label={t('fontes.reconnectPreventive', { source: name })}
          >
            {t('fontes.banner.dataGapCta')}
          </button>
        </span>
      </div>
    );
  }
  return null;
}

/** Ações no footer — variam por estado */
function FontesCardActions({
  variant,
  type,
  folderId,
  onConnect,
  onDisconnect,
  onPickFolder,
}: {
  readonly variant: FontesCardVariant;
  readonly type: FontesSourceType;
  readonly folderId?: string | null | undefined;
  readonly onConnect: () => void;
  readonly onDisconnect: () => void;
  readonly onPickFolder?: (() => void) | undefined;
}): JSX.Element {
  const name = sourceDisplayName(type);

  if (variant === 'connected') {
    // Google Drive sem pasta: mostrar "Selecionar pasta" como ação principal + Desconectar secundário
    if (type === 'google_drive' && (folderId == null || folderId === '')) {
      return (
        <div className="fontes-actions">
          <button
            type="button"
            className="fontes-btn fontes-btn--tonal"
            onClick={onPickFolder}
            aria-label={t('sources.selectFolder')}
          >
            <MsIcon name="folder_open" className="fontes-btn-icon" />
            {t('sources.selectFolder')}
          </button>
          <button
            type="button"
            className="fontes-btn fontes-btn--menu"
            onClick={onDisconnect}
            aria-label={`${t('sources.disconnect')} ${name}`}
            title={t('sources.disconnect')}
          >
            <MsIcon name="link_off" className="fontes-btn-icon" />
          </button>
        </div>
      );
    }
    // Google Drive com pasta ja selecionada: "Trocar pasta" + Desconectar
    if (type === 'google_drive' && folderId != null && folderId !== '') {
      return (
        <div className="fontes-actions">
          <button
            type="button"
            className="fontes-btn fontes-btn--tonal"
            onClick={onPickFolder}
            aria-label={t('sources.changeFolder')}
          >
            <MsIcon name="folder_open" className="fontes-btn-icon" />
            {t('sources.changeFolder')}
          </button>
          <button
            type="button"
            className="fontes-btn fontes-btn--menu"
            onClick={onDisconnect}
            aria-label={`${t('sources.disconnect')} ${name}`}
            title={t('sources.disconnect')}
          >
            <MsIcon name="link_off" className="fontes-btn-icon" />
          </button>
        </div>
      );
    }
    // Fonte conectada com configuração disponível: "Configurar" como primária,
    // "Desconectar" como ação secundária discreta (ícone apenas).
    // FLAG: quando rota /sources/:id/configure existir, substituir o onClick por navigate().
    return (
      <div className="fontes-actions">
        <button
          type="button"
          className="fontes-btn fontes-btn--tonal"
          onClick={() => {
            /* FLAG: navegar para /sources/:id/configure quando a rota existir */
          }}
          aria-label={`Configurar ${name}`}
        >
          <MsIcon name="settings" className="fontes-btn-icon" />
          {/* copy inline — chave fontes.actions.configure ainda não existe em i18n */}
          Configurar
        </button>
        <button
          type="button"
          className="fontes-btn fontes-btn--menu"
          onClick={onDisconnect}
          aria-label={`${t('sources.disconnect')} ${name}`}
          title={t('sources.disconnect')}
        >
          <MsIcon name="link_off" className="fontes-btn-icon" />
        </button>
      </div>
    );
  }

  if (variant === 'warn') {
    return (
      <div className="fontes-actions">
        <button
          type="button"
          className="fontes-btn fontes-btn--outlined"
          onClick={onPickFolder ?? onConnect}
          aria-label={t('fontes.actions.verifyFolder')}
        >
          <MsIcon name="manage_search" className="fontes-btn-icon" />
          {t('fontes.actions.verifyFolder')}
        </button>
      </div>
    );
  }

  if (variant === 'disconnected') {
    return (
      <div className="fontes-actions">
        <button
          type="button"
          className="fontes-btn fontes-btn--reconnect"
          onClick={onConnect}
          aria-label={t('fontes.reconnect', { source: name })}
        >
          <MsIcon name="link" className="fontes-btn-icon" />
          {t('fontes.reconnect', { source: name })}
        </button>
      </div>
    );
  }

  // data-gap
  return (
    <div className="fontes-actions">
      <button
        type="button"
        className="fontes-btn fontes-btn--tonal"
        onClick={onConnect}
        aria-label={t('fontes.reconnectPreventive', { source: name })}
      >
        <MsIcon name="link" className="fontes-btn-icon" />
        {t('fontes.reconnectPreventiveShort')}
      </button>
    </div>
  );
}

/** Sync icon name por estado */
function syncIconName(variant: FontesCardVariant): string {
  return variant === 'disconnected' || variant === 'warn' ? 'sync_problem' : 'sync';
}

/** Conteúdo condicional: feeds ou kpi-inline (data-gap) */
function FontesCardBody({
  type,
  variant,
}: {
  readonly type: FontesSourceType;
  readonly variant: FontesCardVariant;
}): JSX.Element {
  // Para data-gap mostramos placeholder de KPI inline (sem dados reais de API neste momento)
  // FLAG: quando API retornar métricas inline, substituir por dados reais
  if (variant === 'data-gap') {
    return (
      <div className="fontes-kpi-inline">
        <div className="fontes-kpi-item">
          <span className="fontes-kpi-label">{t('fontes.kpi.reach')}</span>
          <span
            className="fontes-kpi-value fontes-kpi-value--gap"
            aria-label={t('fontes.kpi.unavailable')}
          >
            &mdash;
          </span>
          <span className="fontes-kpi-gap-chip" aria-label={t('fontes.kpi.noData')}>
            <MsIcon name="info" className="fontes-kpi-gap-chip-icon" />
            {t('fontes.kpi.noData')}
          </span>
        </div>
        <div className="fontes-kpi-item">
          <span className="fontes-kpi-label">{t('fontes.kpi.impressions')}</span>
          <span
            className="fontes-kpi-value fontes-kpi-value--gap"
            aria-label={t('fontes.kpi.unavailable')}
          >
            &mdash;
          </span>
          <span className="fontes-kpi-gap-chip" aria-label={t('fontes.kpi.noData')}>
            <MsIcon name="info" className="fontes-kpi-gap-chip-icon" />
            {t('fontes.kpi.noData')}
          </span>
        </div>
      </div>
    );
  }

  const feeds = FEEDS_BY_TYPE[type];
  return (
    <div className="fontes-feeds">
      <div className="fontes-feeds-label" aria-hidden="true">
        {t('fontes.collectsLabel')}
      </div>
      <ul className="fontes-feeds-list" aria-label={t('fontes.collectsLabel')}>
        {feeds.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** Componente principal — FontesCard */
export function FontesCard({
  sourceId,
  type,
  variant,
  accountLabel,
  lastSyncLabel,
  onConnect,
  onDisconnect,
  onPickFolder,
  folderId,
  folderName: _folderName,
  staggerIndex: _staggerIndex,
}: FontesCardProps): JSX.Element {
  const name = sourceDisplayName(type);
  const iconName = ICON_BY_TYPE[type];

  const ariaLabel = `${name} — ${
    variant === 'connected'
      ? t('fontes.status.connected')
      : variant === 'warn'
        ? t('fontes.status.warn')
        : variant === 'disconnected'
          ? t('fontes.status.disconnected')
          : t('fontes.status.dataGap')
  }`;

  return (
    <article className={cardClass(variant)} aria-label={ariaLabel} data-source-id={sourceId}>
      {/* Filete tricolor de 3px no topo — assinatura CROSS (D-04) */}
      <div className="fontes-card-edge" aria-hidden="true" />

      {/* Header: ícone + identidade + status chip */}
      <div className="fontes-card-header">
        <div className={iconWrapClass(type, variant)} aria-hidden="true">
          <MsIcon name={iconName} className="fontes-icon" />
        </div>

        <div className="fontes-identity">
          <div className="fontes-name">{name}</div>
          {accountLabel !== null && accountLabel !== '' ? (
            <div className="fontes-account">{accountLabel}</div>
          ) : null}
        </div>

        <FontesStatusChip variant={variant} />
      </div>

      {/* Body: feeds ou kpi inline (data-gap) */}
      <FontesCardBody type={type} variant={variant} />

      {/* Banners de estado */}
      <FontesStateBanner
        variant={variant}
        lastSyncLabel={lastSyncLabel}
        type={type}
        onConnect={onConnect}
      />

      {/* Footer: sync info + ações */}
      <div className="fontes-card-footer">
        <div className="fontes-sync">
          <MsIcon
            name={syncIconName(variant)}
            className={`fontes-sync-icon${variant === 'disconnected' ? ' fontes-sync-icon--error' : ''}`}
          />
          {lastSyncLabel}
        </div>
        <FontesCardActions
          variant={variant}
          type={type}
          folderId={folderId}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
          onPickFolder={onPickFolder}
        />
      </div>
    </article>
  );
}

/** Card "Adicionar fonte" */
interface FontesAddCardProps {
  readonly onAdd: () => void;
}

export function FontesAddCard({ onAdd }: FontesAddCardProps): JSX.Element {
  return (
    <div
      className="fontes-card fontes-card--add"
      role="button"
      tabIndex={0}
      onClick={onAdd}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd();
        }
      }}
      aria-label={t('fontes.addSource')}
    >
      <div className="fontes-add-inner">
        <div className="fontes-add-icon-wrap" aria-hidden="true">
          <MsIcon name="add_circle" className="fontes-add-icon" />
        </div>
        <div className="fontes-add-title">{t('fontes.addTitle')}</div>
        <div className="fontes-add-desc">{t('fontes.addDesc')}</div>
        <div className="fontes-add-cta" aria-hidden="true">
          <MsIcon name="arrow_forward" className="fontes-add-cta-icon" />
          {t('fontes.addCta')}
        </div>
      </div>
    </div>
  );
}
